/**
 * Mounts ViewGridView for an active view carrying a `grid` clause. Same shape as
 * ViewMatrixContainer: the projection comes from the SAME machinery the board uses
 * (`useViewSurface` → the EFFECTIVE view + eval ctx), so governance, live knobs,
 * view-INSTANCE parent resolution, the plane pick, and the crash guard (a throwing
 * projection renders nothing + a HUD diagnostic, never an unmounted tree) all
 * apply. The grid is display-only — a cross-tab has no cell-level write target.
 */
import { useMemo, type ReactNode } from "react";
import { computeGridData } from "@archlang/engine";
import { useViewSurface } from "./useViewSurface.js";
import { ViewGridView } from "./ViewGridView.js";
import { useRegisterViewExport } from "./ViewExportContext.js";
import type { ViewExportAction } from "./export/ViewExportMenu.js";
import { gridToCsv, downloadText } from "./export/viewExport.js";
import { useTranslation } from "../i18n";

export function ViewGridContainer(): ReactNode {
  const { t } = useTranslation();
  const { view, enginePlan, ctx } = useViewSurface();
  const data = useMemo(() => {
    if (!view || !ctx) return null;
    try {
      return computeGridData(view, enginePlan, ctx);
    } catch (e) {
      // Same crash posture as the board's plan computation — degrade, never
      // unmount the tree.
      console.debug("[view-grid] grid data evaluation failed:", e);
      return null;
    }
  }, [view, enginePlan, ctx]);

  const exportActions = useMemo<ViewExportAction[]>(
    () =>
      data
        ? [{ label: t("indexExportCsv"), run: () => downloadText(gridToCsv(data), "grid.csv", "text/csv") }]
        : [],
    [data, t],
  );
  useRegisterViewExport(exportActions);

  if (!data) return null;
  return <ViewGridView data={data} focus={enginePlan?.focus} />;
}
